import { inject, Injectable, signal } from "@angular/core";
import { Observable, of, tap, map, switchMap } from "rxjs";
import { MediaAsset, MediaAssetQueryParams } from "@models/media-asset.model";
import { MediaAssetApiService, TrimMediaAssetRequest, TrimMediaAssetResponse, UpdateMediaAssetRequest, VerticalMediaAssetRequest, VerticalMediaAssetResponse, RecutMediaAssetRequest, RecutMediaAssetResponse } from "./media-asset-api.service";
import { environment } from "@environment";

interface PublisherCache {
  assets: MediaAsset[]
  timestamp: number
}

@Injectable({
  providedIn: 'root',
})
export class MediaAssetHelperService{
    private mediaAssetApiService = inject(MediaAssetApiService)
    private publisherCache = new Map<string, PublisherCache>()
    private readonly PUBLISHER_CACHE_TTL = 5 * 60 * 1000

    allAssets = signal<MediaAsset[]>([])

    getAllAssets = this.allAssets.asReadonly()

    loadAllAssets(filters?: MediaAssetQueryParams): Observable<MediaAsset[]> {
      return this.mediaAssetApiService.getAllMediaAssets$(filters).pipe(
        tap((response) => {
          const sortedAssets = response.assets.sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          })
          this.setAllAssets(sortedAssets);
        }),
        map(response => response.assets)
      )
    }

    setAllAssets(assets: MediaAsset[]): void {
        this.allAssets.set(assets)
        this.prefetchThumbnails(assets)
    }

    private prefetchThumbnails(assets: MediaAsset[]): void {
      assets.forEach(asset => {
        let url: string
        if ((asset.bucketName === 'media-hub-clips' || asset.bucketName === 'media-library') &&
            asset.generatedThumbnails?.length && asset.generatedThumbnails[0].url) {
          url = asset.generatedThumbnails[0].url
        } else {
          url = `${environment.cdnUrl}?id=${asset.id}&t=t`
        }
        const img = new Image()
        img.src = url
      })
    }

    deleteAsset(assetId: string): Observable<{ message: string }> {
      return this.mediaAssetApiService.deleteMediaAsset$(assetId).pipe(
        tap(() => {
          const currentAssets = this.allAssets()
          const deletedAsset = currentAssets.find(a => a.id === assetId)
          const updatedAssets = currentAssets.filter(asset => asset.id !== assetId)
          this.allAssets.set(updatedAssets)
          if (deletedAsset?.publisherId) {
            this.invalidatePublisherCache(deletedAsset.publisherId)
          }
        })
      )
    }

    updateAsset(assetId: string, request: UpdateMediaAssetRequest): Observable<MediaAsset> {
      return this.mediaAssetApiService.updateMediaAsset$(assetId, request).pipe(
        tap(() => {
          const currentAssets = this.allAssets()
          const updatedAssets = currentAssets.map(asset => {
            if (asset.id === assetId) {
              const updatedAsset: MediaAsset = { ...asset }

              if (request.name !== undefined) {
                updatedAsset.name = request.name
              }
              if (request.description !== undefined) {
                updatedAsset.description = request.description
              }
              if (request.tags !== undefined) {
                updatedAsset.tags = request.tags
              }

              return updatedAsset
            }
            return asset
          })
          this.allAssets.set(updatedAssets)
        }),
        map(() => {
          const updatedAsset = this.allAssets().find(asset => asset.id === assetId)!
          return updatedAsset
        })
      )
    }

    getPrivateContentId() : string{
      return this.allAssets().filter(x=>x.name === 'Private')[0].id
    }

    getAssetsByPublisherId(publisherId: string, forceRefresh = false): Observable<MediaAsset[]> {
      const cached = this.publisherCache.get(publisherId)
      if (!forceRefresh && cached && (Date.now() - cached.timestamp) < this.PUBLISHER_CACHE_TTL) {
        return of(cached.assets)
      }

      return this.mediaAssetApiService.getAllMediaAssets$({ publisherId }).pipe(
        map(response => {
          const sortedAssets = response.assets.sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          })
          this.publisherCache.set(publisherId, { assets: sortedAssets, timestamp: Date.now() })
          this.addAssetsToAllAssets(sortedAssets)
          this.prefetchThumbnails(sortedAssets)
          return sortedAssets
        })
      )
    }

    invalidatePublisherCache(publisherId: string): void {
      this.publisherCache.delete(publisherId)
    }

    getAssetsByIds(ids: string[]): Observable<MediaAsset[]> {
      if (!ids || ids.length === 0) {
        return new Observable(observer => {
          observer.next([])
          observer.complete()
        })
      }

      return this.mediaAssetApiService.getMediaAssetsByIds$(ids).pipe(
        tap(response => {
          this.addAssetsToAllAssets(response.assets)
        }),
        map(response => {
          const assetMap = new Map(response.assets.map(asset => [asset.id, asset]))
          return ids.map(id => assetMap.get(id)).filter((asset): asset is MediaAsset => asset !== undefined)
        })
      )
    }

    addAssetsToAllAssets(assets: MediaAsset[]): void {
      const currentAssets = this.allAssets()
      const currentAssetIds = new Set(currentAssets.map(a => a.id))
      const newAssets = assets.filter(asset => !currentAssetIds.has(asset.id))

      if (newAssets.length > 0) {
        const updatedAssets = [...currentAssets, ...newAssets].sort((a, b) => {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        })
        this.allAssets.set(updatedAssets)
        this.prefetchThumbnails(newAssets)
      }
    }

    async downloadVisualDescription(assetId: string): Promise<void> {
      const result = await this.mediaAssetApiService.downloadVisualDescription(assetId)
      if (result.body) {
        this.triggerDownload(result.body, result.fileName || `${assetId}-vds3-record.json`)
      }
    }

    /** One analysis document of an asset, or null when the asset has none. */
    async getAnalysisDoc<T>(assetId: string, doc: string): Promise<T | null> {
      try {
        const res = await this.mediaAssetApiService.getAnalysisDoc<T>(assetId, doc)
        return res?.data ?? null
      } catch {
        // a film has no match: the missing document is an ordinary answer
        return null
      }
    }

    async downloadTranscription(assetId: string): Promise<void> {
      const result = await this.mediaAssetApiService.downloadTranscription(assetId)
      if (result.body) {
        this.triggerDownload(result.body, result.fileName || `transcription-${assetId}.txt`)
      }
    }

    private triggerDownload(blob: Blob, fileName: string): void {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    }

    /** A clip cut at the user's own seconds; resolves with the new clip asset once it exists. */
    recutMediaAsset(assetId: string, request: RecutMediaAssetRequest): Observable<{ response: RecutMediaAssetResponse; newAsset: MediaAsset }> {
      return this.mediaAssetApiService.recutMediaAsset$(assetId, request).pipe(
        switchMap(response =>
          this.getAssetsByIds([response.mediaAsset.id]).pipe(
            map(assets => ({
              response,
              newAsset: assets[0],
            }))
          )
        )
      )
    }

    /** A 9:16 rendering of a clip; resolves with the new clip asset once it exists. */
    verticalMediaAsset(assetId: string, request: VerticalMediaAssetRequest = {}): Observable<{ response: VerticalMediaAssetResponse; newAsset: MediaAsset }> {
      return this.mediaAssetApiService.verticalMediaAsset$(assetId, request).pipe(
        switchMap(response =>
          this.getAssetsByIds([response.mediaAsset.id]).pipe(
            map(assets => ({
              response,
              newAsset: assets[0],
            }))
          )
        )
      )
    }

    trimMediaAsset(assetId: string, request: TrimMediaAssetRequest): Observable<{ trimResponse: TrimMediaAssetResponse; newAsset: MediaAsset }> {
      return this.mediaAssetApiService.trimMediaAsset$(assetId, request).pipe(
        switchMap(trimResponse => {
          const newAssetId = trimResponse.mediaAsset.id
          return this.getAssetsByIds([newAssetId]).pipe(
            map(assets => ({
              trimResponse,
              newAsset: assets[0]
            }))
          )
        })
      )
    }
}